import { EditorState, EditorThemeClasses, Klass, LexicalEditor, LexicalNode, LexicalNodeReplacement } from 'lexical'
import React, { ErrorInfo, ForwardRefExoticComponent, PropsWithChildren, PropsWithoutRef, ReactNode, RefAttributes } from 'react'

import { InitialEditorStateType, LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { defaultLexicalTheme } from '../defaultLexicalTheme'
import { getInitialLexicalEditorState } from '../utils'
import { OnTextContentChangePlugin } from '../Plugins/OnTextContentChangePlugin/OnTextContentChangePlugin'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin'
import { TablePlugin } from '@lexical/react/LexicalTablePlugin'
import './WritableLexicalEditor.scss'
import { ErrorBoundary } from 'react-error-boundary'

const onLexicalError = (error: unknown) => {
  console.error(error)
}

export interface WritableLexicalEditorProps {
  initialEditorState?: InitialEditorStateType
  initialHtmlValue?: string
  initialTextValue?: string
  theme?: EditorThemeClasses
  onChange?: (editorState: EditorState, editor: LexicalEditor, tags: Set<string>) => void
  onTextContentChange?: (textContent: string) => void
  placeholder?: JSX.Element | null
  className?: string
  containerClassName?: string
  editorContainerClassName?: string
  lexicalNodes?: ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement>
  header?: ReactNode
  footer?: ReactNode
  onGeneralError?: (error: Error, info: ErrorInfo) => void
  errorFallback?: ReactNode
}

export const WritableLexicalEditor: ForwardRefExoticComponent<PropsWithoutRef<PropsWithChildren<WritableLexicalEditorProps>> & RefAttributes<LexicalEditor>> =
  React.forwardRef<LexicalEditor, PropsWithChildren<WritableLexicalEditorProps>>(
    (
      {
        initialEditorState,
        initialHtmlValue,
        initialTextValue,
        onChange,
        onTextContentChange,
        placeholder = null,
        theme,
        lexicalNodes,
        className = '',
        containerClassName = '',
        editorContainerClassName = '',
        header,
        footer,
        onGeneralError,
        errorFallback = null,
        children,
      },
      ref,
    ) => {
      const initialConfig = {
        namespace: 'WritableEditor',
        theme: theme ?? defaultLexicalTheme,
        onError: onLexicalError,
        editorState: getInitialLexicalEditorState(initialEditorState, initialHtmlValue, initialTextValue),
        nodes: lexicalNodes ?? [],
      }

      return (
        <ErrorBoundary fallback={errorFallback} onError={onGeneralError}>
          <div className={`form-control d-flex ${className}`}>
            <LexicalComposer initialConfig={initialConfig}>
              <div className={`mw-0 d-flex flex-column flex-grow-1 ${containerClassName}`}>
                {header}
                <div className={`position-relative ${editorContainerClassName}`}>
                  <RichTextPlugin
                    contentEditable={<ContentEditable className='flex-grow-1 editor-content-editable' />}
                    placeholder={placeholder ? <div className='editor-placeholder'>{placeholder}</div> : null}
                    ErrorBoundary={LexicalErrorBoundary}
                  />
                </div>
                {footer}
              </div>
              {onChange ? <OnChangePlugin onChange={onChange} /> : null}
              {ref ? <EditorRefPlugin editorRef={ref} /> : null}
              {onTextContentChange ? <OnTextContentChangePlugin onChange={onTextContentChange} /> : null}
              <HistoryPlugin />
              <ListPlugin />
              <TabIndentationPlugin />
              <LinkPlugin />
              <TablePlugin />
              {children}
            </LexicalComposer>
          </div>
        </ErrorBoundary>
      )
    },
  )
